import React from 'react';
import { useTranslation } from 'react-i18next';
import PrintingTaskInterface from './TypeSpecific/PrintingTaskInterface';
import CuttingTaskInterface from './TypeSpecific/CuttingTaskInterface';
import SeamedTaskInterface from './TypeSpecific/SeamedTaskInterface';
import SetupTaskInterface from './TypeSpecific/SetupTaskInterface';
import QualityCheckTaskInterface from './TypeSpecific/QualityCheckTaskInterface';
import MaterialPrepTaskInterface from './TypeSpecific/MaterialPrepTaskInterface';
import { useTaskTypes } from '../../contexts/TaskTypesContext';

const getStatusColor = (status) => {
  const colors = {
    PENDING: '#FFA500',
    IN_PROGRESS: '#2196F3',
    COMPLETED: '#4CAF50',
    CANCELLED: '#F44336',
    PAUSED: '#FF9800',
    ASSIGNED: '#9C27B0',
  };
  return colors[status] || '#9E9E9E';
};

const STATUS_KEY_MAP = {
  IN_PROGRESS: 'taskStatus.inProgress',
  PAUSED: 'taskStatus.paused',
  ASSIGNED: 'taskStatus.assigned',
  COMPLETED: 'taskStatus.completed',
  PENDING: 'taskStatus.pending',
  CANCELLED: 'taskStatus.cancelled',
};

const TASK_TYPE_KEY_MAP = {
  PRINTING: 'taskTypes.printing',
  DIE_CUTTING: 'taskTypes.dieCutting',
  SEAMED: 'taskTypes.seamed',
  CUTTING: 'taskTypes.cutting',
  SETUP: 'taskTypes.setup',
  QUALITY_CHECK: 'taskTypes.qualityCheck',
  MATERIAL_PREP: 'taskTypes.materialPrep',
  LAMINATING: 'taskTypes.laminating',
  PACKAGING: 'taskTypes.packaging',
  STANDARD: 'taskTypes.standard',
};

const TaskExecutionDetailModal = ({ task, onClose }) => {
  const { t } = useTranslation('operator');
  const { taskTypeMap } = useTaskTypes();

  if (!task) return null;

  const renderInterface = () => {
    const props = { task, readOnly: true };
    const specialForm = taskTypeMap[task.task_type]?.special_form || null;
    switch (specialForm) {
      case 'PRINTING':
        return <PrintingTaskInterface {...props} />;
      case 'DIE_CUTTING':
        return <CuttingTaskInterface {...props} />;
      case 'SEAMED':
        return <SeamedTaskInterface {...props} />;
      case 'SETUP':
        return <SetupTaskInterface {...props} />;
      case 'QUALITY_CHECK':
        return <QualityCheckTaskInterface {...props} />;
      case 'MATERIAL_PREP':
        return <MaterialPrepTaskInterface {...props} />;
      default:
        return (
          <div style={{ padding: '40px', textAlign: 'center', color: '#6c757d' }}>
            <p style={{ fontSize: '16px', fontStyle: 'italic' }}>
              {t('supervisorView.noDetailedView', { taskType: task.task_type || 'STANDARD' })}
            </p>
          </div>
        );
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.6)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '20px',
        overflowY: 'auto',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          width: '100%',
          maxWidth: '900px',
          margin: '0 auto',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          overflow: 'hidden',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div
          style={{
            background: 'linear-gradient(135deg, #2c3e50, #34495e)',
            padding: '20px 24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <h2 style={{ margin: 0, fontSize: '18px', color: 'white', fontWeight: 600 }}>
              {task.task_name || task.label}
            </h2>
            <div style={{ marginTop: '8px', display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
              {task.task_code && (
                <span style={{
                  background: 'rgba(255,255,255,0.15)',
                  color: 'white',
                  padding: '2px 8px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  fontFamily: 'monospace',
                }}>
                  {task.task_code}
                </span>
              )}
              {task.status && (
                <span style={{
                  background: getStatusColor(task.status),
                  color: 'white',
                  padding: '2px 8px',
                  borderRadius: '12px',
                  fontSize: '11px',
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                }}>
                  {STATUS_KEY_MAP[task.status] ? t(STATUS_KEY_MAP[task.status]) : task.status}
                </span>
              )}
              {task.metadata?.assigned_user && (
                <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: '13px' }}>
                  👤 {task.metadata.assigned_user}
                </span>
              )}
              {task.task_type && (
                <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px' }}>
                  {TASK_TYPE_KEY_MAP[task.task_type] ? t(TASK_TYPE_KEY_MAP[task.task_type]) : task.task_type}
                </span>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.15)',
              border: 'none',
              color: 'white',
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              cursor: 'pointer',
              fontSize: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            ✕
          </button>
        </div>

        {/* Read-only banner */}
        <div style={{
          background: '#e8f4fd',
          borderBottom: '1px solid #bee3f8',
          padding: '8px 24px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '13px',
          color: '#2b6cb0',
        }}>
          <span>👁</span>
          <span>{t('supervisorView.readOnlyBanner')}</span>
        </div>

        {/* Modal Body */}
        <div style={{ padding: '0', maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
          {renderInterface()}
        </div>
      </div>
    </div>
  );
};

export default TaskExecutionDetailModal;
